//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Download, X, Copy, Check } from 'lucide-react';
// Use the same Loop Genie mascot icon shown on chat message avatars
// (AnimatedAIIcon in AIResponse.tsx) instead of the generic "AI+" logo, so
// the header icon and the in-chat avatar are visually the same character.
import LoopGenieIcon from '../../../icons/AIChatLogo.svg';
import { Button } from '../../ui/button';
import { Separator } from '../../ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../ui/tooltip';
import { Tabs, TabsList, TabsTrigger } from '../../ui/tabs';
import { cn } from '../../../lib/utils';
import { cardStyles } from '../../../styles/LoopStyles';
import { renderTaskIcon, type TaskIcon } from './taskIcon';

export interface ChatHeaderProps {
  darkMode: boolean;
  selectedLoopName: string | null;
  isAIBuffering: boolean;
  selectedLoopId: string | null;
  activeTab: string;
  onTabChange: (tab: string) => void;
  onNewChat: () => void;
  onDownload: () => void;
  onClose: () => void;
  threadId?: string;
  tracingEnabled?: boolean;
  /** Whether the current loop has an active conversation with exportable
   * messages. Gates the download button so it isn't clickable on the initial
   * welcome screen (or any loop with no chat history yet); defaults to false
   * so the button is disabled unless a caller explicitly proves otherwise. */
  hasChatHistory?: boolean;
  /** The Task Loop Genie is currently scoped to, if one is selected. Rendered
   * as a persistent badge so it stays visible while the conversation scrolls
   * (unlike the one-time task banner at the top of ChatThread's history). */
  activeTaskInfo?: { name: string; icon?: TaskIcon } | null;
}

const ChatHeader: React.FC<ChatHeaderProps> = ({
  darkMode,
  selectedLoopName,
  isAIBuffering,
  selectedLoopId,
  activeTab,
  onTabChange,
  onNewChat,
  onDownload,
  onClose,
  threadId,
  tracingEnabled,
  activeTaskInfo,
  hasChatHistory = false,
}) => {
  const { t } = useTranslation();
  const [threadIdCopied, setThreadIdCopied] = useState(false);
  const copyResetTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => clearTimeout(copyResetTimeoutRef.current);
  }, []);

  const handleCopyThreadId = async () => {
    if (!threadId) return;
    try {
      await navigator.clipboard.writeText(threadId);
      setThreadIdCopied(true);
      copyResetTimeoutRef.current = setTimeout(
        () => setThreadIdCopied(false),
        1500,
      );
    } catch {
      // Clipboard write can fail (permissions, insecure context) — badge
      // stays visible, there's just no copied-confirmation feedback.
    }
  };
  const showThreadIdBadge = Boolean(tracingEnabled && threadId);

  return (
    <>
      <div
        id="id-ai-module-header"
        data-testid="ai-module-header"
        style={{
          ...cardStyles.aiModuleHeaderWithGap,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: darkMode
            ? 'rgba(30, 30, 30, 0.9)'
            : 'rgba(211, 217, 222, 0.2)',
        }}
      >
        <div
          id="id-ai-module-logo-section"
          data-testid="ai-module-logo-section"
          style={cardStyles.flexRowCenter}
        >
          <img
            src={LoopGenieIcon}
            alt={t('ai-logo')}
            style={cardStyles.aiLogoImage}
          />
          <div
            id="id-ai-module-header-inner"
            data-testid="ai-module-header-inner"
            style={cardStyles.aiModuleHeaderInner}
          >
            <p
              style={{
                ...cardStyles.AIcardTitle,
                ...(selectedLoopName
                  ? cardStyles.aiTitleWithLoop
                  : cardStyles.aiTitleContainer),
                ...(darkMode && { color: '#e0e0e0' }),
              }}
            >
              <span
                id="id-ai-module-title-text"
                data-testid="ai-module-title-text"
                style={{
                  ...cardStyles.aiTitleLoopText,
                  ...(darkMode && { color: '#e0e0e0' }),
                }}
              >
                {t('loop-genie')}
              </span>
              {selectedLoopName && (
                <span
                  id="id-ai-module-loop-name"
                  data-testid="ai-module-loop-name"
                  style={{
                    ...cardStyles.aiTitleLoopName,
                    ...(darkMode && { color: '#4fc3f7' }),
                  }}
                >
                  | {selectedLoopName}
                </span>
              )}
            </p>
            {activeTaskInfo && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      id="id-ai-active-task-badge"
                      data-testid="ai-active-task-badge"
                      className={cn(
                        'ml-2 flex w-fit shrink-0 cursor-default items-center gap-1.5 rounded-full border-0 px-2 py-0.5 text-xs font-medium',
                        darkMode
                          ? 'bg-white/10 text-[#b0b0b0]'
                          : 'bg-black/5 text-gray-600',
                      )}
                    >
                      <span
                        className="flex size-4 shrink-0 items-center justify-center overflow-hidden rounded-full text-[10px] leading-none"
                        style={{
                          background: activeTaskInfo.icon?.color || '#E8F0FE',
                        }}
                      >
                        {renderTaskIcon(
                          activeTaskInfo.icon,
                          activeTaskInfo.name,
                          { size: 12, testId: 'ai-header-task-emoji' },
                        )}
                      </span>
                      <span
                        data-testid="ai-active-task-badge-name"
                        className="max-w-[160px] truncate"
                      >
                        {activeTaskInfo.name}
                      </span>
                      {isAIBuffering && (
                        <span
                          data-testid="ai-active-task-badge-pulse"
                          aria-hidden="true"
                          className="size-1.5 shrink-0 rounded-full bg-emerald-500 motion-safe:animate-pulse"
                        />
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {isAIBuffering
                      ? t(
                          'working-on-task-tooltip-prefix',
                          'Loop Genie is working on:',
                        )
                      : t('active-task-tooltip-prefix', 'Active task:')}{' '}
                    {activeTaskInfo.name}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
        <div
          id="id-ai-module-header-actions"
          data-testid="ai-module-header-actions"
          className="flex items-center gap-1"
        >
          <TooltipProvider>
            {showThreadIdBadge && (
              <div
                id="id-ai-thread-id-badge"
                data-testid="ai-thread-id-badge"
                className={cn(
                  'flex items-center gap-1 rounded-full bg-black/5 px-2 py-0.5 text-xs text-gray-500',
                  darkMode && 'bg-white/10 text-[#b0b0b0]',
                )}
              >
                <span title={threadId} className="font-mono">
                  {t('thread-id-label')}: {threadId!.slice(0, 8)}…
                </span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={handleCopyThreadId}
                      aria-label={t('copy-thread-id')}
                      data-testid="ai-thread-id-copy-button"
                      className={cn(
                        'size-5 p-0.5 text-gray-500 hover:bg-black/10 hover:text-gray-700',
                        darkMode &&
                          'text-[#b0b0b0] hover:bg-white/10 hover:text-white',
                      )}
                    >
                      {threadIdCopied ? (
                        <Check
                          data-testid="ThreadIdCheckIcon"
                          className="size-3"
                        />
                      ) : (
                        <Copy
                          data-testid="ThreadIdCopyIcon"
                          className="size-3"
                        />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('copy-thread-id')}</TooltipContent>
                </Tooltip>
              </div>
            )}
            {selectedLoopId && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    id="id-ai-module-new-chat-wrapper"
                    data-testid="ai-module-new-chat-wrapper"
                  >
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={onNewChat}
                      disabled={isAIBuffering}
                      aria-label={t('new-chat') || 'New Chat'}
                      className="size-8 text-gray-500 hover:bg-black/5 hover:text-gray-700"
                    >
                      <Plus className="size-4" />
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {isAIBuffering
                    ? t('processing-wait-to-new-chat')
                    : t('new-chat')}
                </TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  id="id-ai-module-download-wrapper"
                  data-testid="ai-module-download-wrapper"
                >
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={onDownload}
                    disabled={!hasChatHistory}
                    aria-label={t('download-chats')}
                    data-testid="ai-download-button"
                    className="size-8 text-gray-500 hover:bg-black/5 hover:text-gray-700"
                  >
                    <Download className="size-4" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {hasChatHistory
                  ? t('download-chats')
                  : t('no-loop-to-save-chats')}
              </TooltipContent>
            </Tooltip>
            <Separator
              id="id-ai-module-separator"
              data-testid="ai-module-separator"
              orientation="vertical"
              className="mx-1 h-5"
            />
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  aria-label={t('close')}
                  className="size-8 text-gray-500 hover:bg-black/5 hover:text-gray-700"
                >
                  <X className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('close')}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      <div
        id="id-ai-module-tabs-wrapper"
        data-testid="ai-module-tabs-wrapper"
        style={{
          padding: '0 16px',
          borderBottom: darkMode ? '1px solid #3f3f3f' : undefined,
        }}
      >
        <Tabs value={activeTab} onValueChange={onTabChange}>
          <TabsList
            aria-label={t('ai-module-tabs')}
            className="h-auto justify-start gap-1 border-none bg-transparent p-0"
          >
            <TabsTrigger
              value="chats"
              className={cn(
                'rounded-none p-2 font-medium',
                darkMode && 'text-[#b0b0b0]',
              )}
            >
              {t('chat') || 'Chat'}
            </TabsTrigger>
            {selectedLoopId && (
              <TabsTrigger
                value="tasks"
                className={cn(
                  'rounded-none p-2 font-medium',
                  darkMode && 'text-[#b0b0b0]',
                )}
              >
                {t('tasks') || 'Tasks'}
              </TabsTrigger>
            )}
            {selectedLoopId && (
              <TabsTrigger
                value="history"
                className={cn(
                  'rounded-none p-2 font-medium',
                  darkMode && 'text-[#b0b0b0]',
                )}
              >
                {t('history') || 'History'}
              </TabsTrigger>
            )}
            {selectedLoopId && (
              <TabsTrigger
                value="mcp"
                className={cn(
                  'rounded-none p-2 font-medium',
                  darkMode && 'text-[#b0b0b0]',
                )}
              >
                {t('mcp-connections-tab') || 'MCP Connections'}
              </TabsTrigger>
            )}
          </TabsList>
        </Tabs>
      </div>
    </>
  );
};

export default ChatHeader;
